import {
  FETCH_OTHER_VENDORS_REQUEST,
  FETCH_OTHER_VENDORS_SUCCESS,
  FETCH_OTHER_VENDORS_FAILURE,
  FETCH_OTHER_VENDOR_BY_ID_REQUEST,
  FETCH_OTHER_VENDOR_BY_ID_SUCCESS,
  FETCH_OTHER_VENDOR_BY_ID_FAILURE,
  CREATE_OTHER_VENDOR_REQUEST,
  CREATE_OTHER_VENDOR_SUCCESS,
  CREATE_OTHER_VENDOR_FAILURE,
  UPDATE_OTHER_VENDOR_REQUEST,
  UPDATE_OTHER_VENDOR_SUCCESS,
  UPDATE_OTHER_VENDOR_FAILURE,
  DELETE_OTHER_VENDOR_REQUEST,
  DELETE_OTHER_VENDOR_SUCCESS,
  DELETE_OTHER_VENDOR_FAILURE,
  CLEAR_OTHER_VENDORS_ERROR,
  SET_SELECTED_OTHER_VENDOR,
  CLEAR_SELECTED_OTHER_VENDOR,
} from '../../constants/redux';

import { OtherVendor } from '../../types/otherVendors';

interface VendorForm {
  id: string | null;
  name: string;
  description: string;
  vendor_url: string;
  errorName: boolean;
  errorDescription: boolean;
  avatarPath: string;
  avatarFile: File | null;
  spinner: boolean;
  redirectTo: string;
  isFormUpdated: boolean;
}

interface OtherVendorsState {
  vendors: OtherVendor[];
  selectedVendor: OtherVendor | null;
  vendorForm: VendorForm;
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  error: string | null;
}

const initialState: OtherVendorsState = {
  vendors: [],
  selectedVendor: null,
  vendorForm: {
    id: null,
    name: '',
    description: '',
    vendor_url: '',
    errorName: false,
    errorDescription: false,
    avatarPath: '',
    avatarFile: null,
    spinner: false,
    redirectTo: '',
    isFormUpdated: false,
  },
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  error: null,
};

export const otherVendorsReducer = (
  state = initialState,
  action: any
): OtherVendorsState => {
  switch (action.type) {
    case FETCH_OTHER_VENDORS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FETCH_OTHER_VENDORS_SUCCESS:
      return {
        ...state,
        loading: false,
        vendors: action.payload,
        error: null,
      };

    case FETCH_OTHER_VENDORS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case FETCH_OTHER_VENDOR_BY_ID_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FETCH_OTHER_VENDOR_BY_ID_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedVendor: action.payload,
        error: null,
        vendorForm: {
          ...state.vendorForm,
          id: action.payload.id.toString(),
          name: action.payload.name || '',
          description: action.payload.description || '',
          vendor_url: action.payload.vendor_url || '',
          avatarPath: action.payload.vendor_logo || '',
          isFormUpdated: false,
          spinner: false,
        },
      };

    case FETCH_OTHER_VENDOR_BY_ID_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case CREATE_OTHER_VENDOR_REQUEST:
      return {
        ...state,
        creating: true,
        error: null,
        vendorForm: {
          ...state.vendorForm,
          spinner: true,
          redirectTo: '',
        },
      };

    case CREATE_OTHER_VENDOR_SUCCESS:
      return {
        ...state,
        creating: false,
        vendors: [...state.vendors, action.payload],
        error: null,
        vendorForm: {
          ...state.vendorForm,
          spinner: false,
          redirectTo: '/vendors/other-vendors',
        },
      };

    case CREATE_OTHER_VENDOR_FAILURE:
      return {
        ...state,
        creating: false,
        error: action.payload,
        vendorForm: {
          ...state.vendorForm,
          spinner: false,
        },
      };

    case UPDATE_OTHER_VENDOR_REQUEST:
      return {
        ...state,
        updating: true,
        error: null,
        vendorForm: {
          ...state.vendorForm,
          spinner: true,
          redirectTo: '',
        },
      };

    case UPDATE_OTHER_VENDOR_SUCCESS:
      return {
        ...state,
        updating: false,
        vendors: state.vendors.map(vendor =>
          vendor.id === action.payload.id ? action.payload : vendor
        ),
        selectedVendor: state.selectedVendor?.id === action.payload.id 
          ? action.payload 
          : state.selectedVendor,
        error: null,
        vendorForm: {
          ...state.vendorForm,
          spinner: false,
          redirectTo: '/vendors/other-vendors',
        },
      };

    case UPDATE_OTHER_VENDOR_FAILURE:
      return {
        ...state,
        updating: false,
        error: action.payload,
        vendorForm: {
          ...state.vendorForm,
          spinner: false,
        },
      };

    case DELETE_OTHER_VENDOR_REQUEST:
      return {
        ...state,
        deleting: true,
        error: null,
      };

    case DELETE_OTHER_VENDOR_SUCCESS:
      return {
        ...state,
        deleting: false,
        vendors: state.vendors.filter(vendor => vendor.id !== action.payload),
        selectedVendor: state.selectedVendor?.id === action.payload 
          ? null 
          : state.selectedVendor,
        error: null,
      };

    case DELETE_OTHER_VENDOR_FAILURE:
      return {
        ...state,
        deleting: false,
        error: action.payload,
      };

    case SET_SELECTED_OTHER_VENDOR:
      return {
        ...state,
        selectedVendor: action.payload,
      };

    case CLEAR_SELECTED_OTHER_VENDOR:
      return {
        ...state,
        selectedVendor: null,
      };

    case CLEAR_OTHER_VENDORS_ERROR:
      return {
        ...state,
        error: null,
      };

    case 'UPDATE_VENDOR_NAME':
      return {
        ...state,
        vendorForm: {
          ...state.vendorForm,
          name: action.payload,
          isFormUpdated: true,
          errorName: false,
        },
      };

    case 'UPDATE_VENDOR_DESCRIPTION':
      return {
        ...state,
        vendorForm: {
          ...state.vendorForm,
          description: action.payload,
          isFormUpdated: true,
          errorDescription: false,
        },
      };

    case 'UPDATE_VENDOR_IMAGE':
      return {
        ...state,
        vendorForm: {
          ...state.vendorForm,
          avatarFile: action.payload,
          isFormUpdated: true,
        },
      };

    case 'UPDATE_VENDOR_URL':
      return {
        ...state,
        vendorForm: {
          ...state.vendorForm,
          vendor_url: action.payload,
          isFormUpdated: true,
        },
      };

    case 'CLEAR_VENDOR_FORM':
      return {
        ...state,
        vendorForm: {
          id: null,
          name: '',
          description: '',
          vendor_url: '',
          errorName: false,
          errorDescription: false,
          avatarPath: '',
          avatarFile: null,
          spinner: false,
          redirectTo: '',
          isFormUpdated: false,
        },
      };

    default:
      return state;
  }
};
